import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import * as Sentry from '@sentry/nextjs';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import {
  MarketplaceLocationSuggestionsDocument,
  type MarketplacePropertySuggestionFragment,
  type MarketplaceZoneType,
} from 'components/marketplace/map/__generated__/location-search.zeus';
import { formatCurrency } from 'helpers/formatters';
import { useDebounce } from 'use-debounce';

import type { MapLocation } from './types';

const zoneTypeLabel = {
  CITY: '(City)',
  COUNTY: '(County)',
  MACRO_NEIGHBORHOOD: '(Neighborhood)',
  NEIGHBORHOOD: '(Neighborhood)',
  RESIDENTIAL_BOUNDARY: '',
  STATE: '',
  SUBNEIGHBORHOOD: '(Neighborhood)',
  UNKNOWN: '',
  ZIP_CODE: '(Zip code)',
} satisfies Record<MarketplaceZoneType, string>;

export interface LocationSearchProps {
  isFocused: boolean;
  selectedLocation?: MapLocation;
  setSelectedLocation: (location?: MapLocation) => void;
  selectedAddress?: MarketplacePropertySuggestionFragment;
  setSelectedAddress: (address?: MarketplacePropertySuggestionFragment) => void;
  /** Input height. `sm` (56px) on the desktop quick-filters bar; `xs` (36px) in
   * the compact mobile header. */
  size?: 'xs' | 'sm' | 'base' | 'lg';
}

const QUERY_STRING_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

type RankedMapLocation = MapLocation & { population?: number };

const reportLocationSuggestionsError = (error: unknown) => {
  Sentry.captureException(error, {
    tags: { feature: 'marketplace-browse', operation: 'locationSuggestions' },
  });
};

const fetchLocationSuggestions = (query: string, signal: AbortSignal) => {
  const types: MarketplaceZoneType[] = /^\d/u.test(query)
    ? ['ZIP_CODE']
    : ['CITY', 'NEIGHBORHOOD', 'SUBNEIGHBORHOOD'];
  return requestZeusFromBrowser(
    MarketplaceLocationSuggestionsDocument,
    { propertyInput: { query }, zoneInput: { query, types } },
    signal,
  );
};

export const MapLocationSearch = ({
  isFocused,
  selectedAddress,
  setSelectedAddress,
  selectedLocation,
  setSelectedLocation,
  size = 'sm',
}: LocationSearchProps) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const resultsRef = useRef<HTMLElement | null>(null);
  const suggestions = useQuery({
    queryKey: ['marketplace', 'location-suggestions', debouncedQuery],
    queryFn: ({ signal }) => fetchLocationSuggestions(debouncedQuery, signal),
    enabled: debouncedQuery.length > QUERY_STRING_MIN_LENGTH,
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30_000,
  });
  const isCurrentQuery = debouncedQuery === query;
  const isFetchingSuggestions =
    query.length > QUERY_STRING_MIN_LENGTH && (!isCurrentQuery || suggestions.isFetching);
  const fetchError = isCurrentQuery && suggestions.isError;
  const data = query.length > QUERY_STRING_MIN_LENGTH ? suggestions.data : undefined;
  const locationOptions = useMemo(() => {
    const rankedLocations: RankedMapLocation[] = (data?.marketplace.searchZones ?? []).map(
      ({ zone }) => ({
        coordinates: zone.center
          ? ([zone.center.longitude, zone.center.latitude] satisfies [number, number])
          : undefined,
        label: [zone.name, zone.stateAbbr].filter(Boolean).join(', '),
        population: zone.population ?? undefined,
        value: zone.id,
        zoneType: zone.type,
      }),
    );
    rankedLocations.sort((a, b) =>
      a.population == null || b.population == null ? 0 : b.population - a.population,
    );
    return rankedLocations.map(({ population: _, ...location }) => location);
  }, [data]);
  const addressOptions = data?.marketplace.addressSearch ?? [];

  useEffect(() => {
    if (suggestions.error) {
      reportLocationSuggestionsError(suggestions.error);
    }
  }, [suggestions.error]);

  useEffect(() => {
    if (resultsRef.current) {
      resultsRef.current.scrollTop = 0;
    }
  }, [suggestions.data, suggestions.error]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsLocationFocused(false);
        setIsDropdownFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLocationSearch = (search: string) => {
    setQuery(search);
  };

  const handleLocationFocus = () => {
    setIsLocationFocused(true);
    setQuery('');
    setSelectedLocation(undefined);
    setSelectedAddress(undefined);
  };
  const handleDropdownFocus = () => setIsDropdownFocused(true);
  const handleDropdownBlur = () => {
    setIsLocationFocused(false);
    setIsDropdownFocused(false);
  };

  const handleLocationSelect = (location: MapLocation) => {
    setSelectedLocation(location);
    setSelectedAddress(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const handleAddressSelect = (address: MarketplacePropertySuggestionFragment) => {
    setSelectedAddress(address);
    setSelectedLocation(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const hasSuggestionResults = locationOptions.length > 0 || addressOptions.length > 0;

  const showNoResultsMessage =
    query.length > QUERY_STRING_MIN_LENGTH &&
    !isFetchingSuggestions &&
    !fetchError &&
    !hasSuggestionResults;

  const shouldShowDropdown =
    Boolean(query) &&
    (isLocationFocused || isDropdownFocused) &&
    (hasSuggestionResults || showNoResultsMessage || isFetchingSuggestions || fetchError);

  return (
    <Col className="w-full" ref={containerRef}>
      <Text as="label" className="text-label-md pb-3 text-od-content-primary max-md:hidden">
        Location
      </Text>
      <Input
        aria-label="Location search"
        id="locations-search"
        name="locations"
        size={size}
        placeholder="Search location"
        onChange={(e) => handleLocationSearch(e.target.value)}
        onFocus={handleLocationFocus}
        defaultValue={selectedAddress?.fullAddress || selectedLocation?.label}
        className={cn('w-full max-md:pr-10', isFocused && 'max-md:pl-10')}
        key={selectedAddress?.fullAddress || selectedLocation?.label}
      />
      <Box id="locations" className="relative w-full">
        <Col
          ref={resultsRef}
          aria-label="Location suggestions"
          className={cn(
            'absolute left-0 top-2 z-[10000] max-h-100 min-h-30 w-full overflow-x-hidden overflow-y-scroll rounded-2xl border border-od-border-accent-brand bg-od-bg-primary',
            shouldShowDropdown ? 'flex' : 'hidden',
          )}
          onFocus={handleDropdownFocus}
          onBlur={handleDropdownBlur}
        >
          {isFetchingSuggestions ? (
            <Box className="w-full px-5 py-5">
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Searching…
              </Text>
            </Box>
          ) : null}
          {fetchError ? (
            <Box className="w-full px-5 py-5" role="alert">
              <Text as="p" className="text-body-sm text-od-content-primary">
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : null}
          {showNoResultsMessage ? (
            <Col className="w-full gap-2 px-5 py-5" role="status" aria-live="polite">
              <Text as="p" className="text-body-sm text-od-content-primary">
                No matches yet
              </Text>
              <Text as="p" className="text-label-sm text-od-content-secondary">
                Try a street number and name, ZIP code, or city. You can also pan the map to search
                this area.
              </Text>
            </Col>
          ) : null}
          {query && locationOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-2">
                Locations
              </Text>
              {locationOptions.map((item, index) => (
                <Col
                  key={item.value}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.label}`}
                    analyticsName="comos-map-select-location"
                    onClick={() => handleLocationSelect(item)}
                    disabled={isFetchingSuggestions || fetchError}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Text as="span" className="text-body-sm">
                      {item.label} {zoneTypeLabel[item.zoneType]}
                    </Text>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
          {query && addressOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-6">
                Listings
              </Text>
              {addressOptions.map((item, index) => (
                <Col
                  key={item.addressId}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.fullAddress}`}
                    analyticsName="cosmos-map-select-address"
                    onClick={() => handleAddressSelect(item)}
                    disabled={isFetchingSuggestions || fetchError}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Col className="items-start">
                      <Text as="p" className="text-body-sm w-full">
                        {item.fullAddress}
                      </Text>
                      {item.listPriceCents && (
                        <Text as="p" className="text-label-sm self-start">
                          {formatCurrency(item.listPriceCents)}
                        </Text>
                      )}
                    </Col>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
        </Col>
      </Box>
    </Col>
  );
};
